import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { GlobalState } from "../../GlobalState";
import Loading from "../utils/loading";
import { literToKiloliter } from "../utils/utils";
import { FaTimesCircle } from "react-icons/fa";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";

// const SensorBar = styled.div`
//   position: relative;
//   flex: 0 0 320px;
//   max-width: 300px;
//   min-width: 300px;
//   margin: 0.5rem;
//   border-radius: 10px;
//   overflow: hidden;
//   border: 1px solid #e5e7eb;
//   display: flex;
//   transition: all 0.3s ease;

//   @media (max-width: 1024px) {
//     flex: 1 1 calc(33.333% - 16px);
//     max-width: calc(33.333% - 16px);
//     min-width: 180px;
//   }

//   @media (max-width: 480px) {
//     flex: 1 1 100%;
//     max-width: 100%;
//     min-width: 0;
//     margin: 0.3rem 0;
//   }
// `;


const SensorBar = styled.div`
  position: relative;
  flex: 0 0 320px;
  max-width: 300px;
  min-width: 300px;
  margin: 0.5rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  transition: all 0.3s ease;

  @media (max-width: 1024px) {
    flex: 1 1 calc(40% - 16px);
    max-width: calc(40% - 16px);
    min-width: 240px;
  }

  @media (max-width: 480px) {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0.3rem 0;
  }
`;

const AccentBar = styled.div`
  width: 5px;
  flex-shrink: 0;
  background: ${({ $isFaulty, $isOnline }) =>
    $isFaulty ? "#F59E0B" : $isOnline ? "#22C55E" : "#94A3B8"};
`;

const SensorInner = styled.div`
  flex: 1;
  min-width: 0;
  padding: 9px 11px;
`;

const popUp = keyframes`
  0%   { opacity: 0; transform: scale(0.4); }
  60%  { opacity: 1; transform: scale(1.15); }
  80%  { transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
`;

const TimestampWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  .tooltip {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(0px);
    background: white;
    color: rgb(204, 72, 77);
    font-size: 0.85rem;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 4px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease, transform 0.2s ease;
    margin-bottom: 6px;
    z-index: 99;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 4px solid transparent;
      border-top-color: white;
    }
  }

  &:hover .tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(-4px);
  }

  &.touch-active .tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(-4px);
  }
`;

/* TOP ROW: location name (left) — value + status (right) */
const TopInfoRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const BadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.08);
  }
`;

const PlainDuration = styled.span`
  display: inline-block; /* Important */
  font-size: 10px;
  font-weight: 500;
  color: #d81111;
  white-space: nowrap;
  animation: ${pulse} 2s infinite ease-in-out;
  transform-origin: center;
`;

/* BOTTOM ROW: bar only, same width as top row */
const BarRow = styled.div`
  width: 100%;
`;

const BarWrapper = styled.div`
  width: 100%;
  min-width: 0;
  height: 24px;
  background: #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    height: 20px;
  }

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ percent }) =>
    `${Math.min(Math.max(percent, 0), 100)}%`};
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: Arial, sans-serif;
  font-weight: 700;
  font-size: 0.75rem;
  border-radius: 8px;
  transition: width 0.5s ease, background 0.3s ease;

  @media (max-width: 768px) {
    font-size: 0.68rem;
  }
`;

const DisplayValue = styled.div`
  font-size: 0.9rem;
  font-family: Arial, sans-serif;
  font-weight: 900;
  color: ${({ color }) => color || "#111827"};
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const Badge = styled.span`
  font-size: 9.5px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $isFaulty, $isOnline }) =>
    $isFaulty
      ? "rgba(245,158,11,.12)"
      : $isOnline
        ? "rgba(34,197,94,.12)"
        : "#F3F4F6"};
  color: ${({ $isFaulty, $isOnline }) =>
    $isFaulty ? "#B45309" : $isOnline ? "#15803D" : "#090a0e"};

  @media (max-width: 768px) {
    font-size: 8px;
    padding: 2px 5px;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgb(57, 56, 56);
  padding: 3px 8px;
  border-radius: 5px;
  flex-shrink: 0;

  ${({ status }) =>
    status === "faulty" &&
    css`
      background-color: rgba(239, 68, 68, 0.5);
    `}

  .icon {
    font-size: 0.85rem;
    line-height: 1;
  }

  .timestamp {
    font-size: 0.65rem;
    font-family: Arial, sans-serif;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: rgb(57, 56, 56);
  }

  @media (max-width: 1024px) {
    font-size: 0.6rem;
    padding: 2px 6px;
    .timestamp { font-size: 0.58rem; }
    .icon { font-size: 0.6rem; }
  }

  @media (max-width: 768px) {
    font-size: 0.55rem;
    padding: 2px 5px;
    gap: 3px;
    .timestamp { font-size: 0.52rem; }
    .icon { font-size: 0.55rem; }
  }
`;

const gradientShift = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const LocationName = styled.p`
  margin: 0;
  font-weight: 700;
  // white-space: nowrap;
  font-size: 0.85rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #111827;
  flex: 1 1 auto;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const getColor = (level, min = 20, max = 80) => {
  if (level < min) return "#ef4444";
  if (level > max) return "#10b981";
  return "#3b82f6";
};

function useTouchTooltip(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let hideTimer;

    const onTouchStart = (e) => {
      e.preventDefault();
      el.classList.add("touch-active");
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => el.classList.remove("touch-active"), 2500);
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      clearTimeout(hideTimer);
    };
  }, [ref]);
}

function StatusTimestamp({ status, date, time }) {
  const wrapperRef = React.useRef(null);
  useTouchTooltip(wrapperRef);

  const label =
    status === "faulty" ? "⚠ Sensor is faulty" : "⚠ Sensor is offline";

  return (
    <StatusBadge status={status}>
      {status === "faulty" ? (
        <FaTimesCircle className="icon" />
      ) : (
        <span className="icon">⚠</span>
      )}

      <TimestampWrapper ref={wrapperRef}>
        <span className="tooltip">{label}</span>
        <span className="timestamp">
          {formatOfflineDuration(date, time)}
        </span>
      </TimestampWrapper>
    </StatusBadge>
  );
}

export default function DashboadSensorBoxV2({ sensor_id, type }) {
  const state = useContext(GlobalState);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);

  useEffect(() => {
    async function fetchData(token, id) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/reading?sensor_id=${id}&type=${type}`,
          { headers: { Authorization: token[0] } }
        );
        setList(res.data);
        setLoading(false);
      } catch (err) {
        console.log(err.message);
      }
    }

    fetchData(state.token, sensor_id);
    const interval = setInterval(
      () => fetchData(state.token, sensor_id),
      8000
    );
    return () => clearInterval(interval);
  }, [state.token, sensor_id, type]);

  if (loading) return <Loading />;

  const waterLevel = parseFloat(list?.volume || 0);
  const isFaulty = list.raw_data === -1;
  const isOnline = list.sensor_status === "online";
  const isOffline = !isFaulty && !isOnline;
  const color = isFaulty
    ? "#ef4444"
    : list?.volume_color || getColor(waterLevel);
  const displayValue = isOffline
    ? "—"
    : list.unit === "litre"
      ? literToKiloliter(list.value)
      : list.value;

  return (
    <SensorBar>
      <AccentBar $isOnline={isOnline} $isFaulty={isFaulty} />
      <SensorInner>
        <TopInfoRow>
          <LocationName>
            {list.location_name?.length > 15
              ? list.location_name.slice(0, 15)
              : list.location_name}
          </LocationName>

          <RightGroup>
            {/* <DisplayValue color={isOffline ? "#9CA3AF" : color}>
              {displayValue}
            </DisplayValue> */}

            {!isOffline && (
              <DisplayValue color={color}>
                {displayValue}
              </DisplayValue>
            )}

            <BadgeStack>
              {isFaulty && <Badge $isFaulty={isFaulty}>faulty</Badge>}
              {isOffline && <Badge $isOnline={false}>offline</Badge>}
              {!isFaulty && isOnline && <Badge $isOnline={isOnline}>online</Badge>}

              {list.sensor_status !== "online" && (
                <PlainDuration>
                  {formatOfflineDuration(list.date, list.time)}
                </PlainDuration>
              )}
            </BadgeStack>
          </RightGroup>
        </TopInfoRow>

        <BarRow>
          <BarWrapper onClick={() => history.push(`/sensor/${sensor_id}/${type}`)}>
            <BarFill percent={waterLevel} color={color} $isOffline={isOffline}>
               {waterLevel}%
            </BarFill>
          </BarWrapper>
        </BarRow>


      </SensorInner>
    </SensorBar>
  );
}